import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { catchError, map, Observable, of, take, timeout, shareReplay } from 'rxjs';
import { environment } from 'src/environments/environment';
import { DataResult } from '../models/data-result.mode';
import { LoggingMessageService } from './logging-message.service';
import { SessionStorageService } from './session-storage.service';
import { SubscriptionChangeService } from './subscription-change.service';
import { GlobalNavigationModel } from '../models/global-header-navigation.model';

@Injectable({ providedIn: 'root' })
export class GlobalHeaderMenuService {
  private readonly localMenuUrl = 'assets/json/global-header-menu-list.json';
  private _BASE_URL: string = environment.FederationApi;
  private _LOCAL_GLOBAL_HEADER_API = environment.LocalJsonApi;
  private _NO_DATA = { message: '' };

  // one-shot cached observable for GlobalNav payload
  private _globalNavOnce$: Observable<GlobalNavigationModel | null> | null = null;

  // In-memory GlobalNav user cache (Nav client scope only)
  private _gnEmail: string = '';
  private _gnFirstName: string = '';
  private _gnLastName: string = '';
  private _gnMessage: string = '';
  private _gnHxUserFlag: boolean = false;

  constructor(private http: HttpClient, private logger: LoggingMessageService, private sessionService: SessionStorageService, private subscriptionService: SubscriptionChangeService) 
  {}

  public setGlobalNavUser(gn: GlobalNavigationModel | null | undefined): void {
    if (!gn) {
      this.clearGlobalNavUser();
      return;
    }
    this._gnEmail = gn.email || '';
    this._gnFirstName = gn.firstname || '';
    this._gnLastName = gn.lastname || '';
    this._gnMessage = gn.message || '';
    this._gnHxUserFlag = (gn.hxuser || '').toString().toLowerCase() === 'true';
  }

  public clearGlobalNavUser(): void {
    this._gnEmail = '';
    this._gnFirstName = '';
    this._gnLastName = '';
    this._gnMessage = '';
    this._gnHxUserFlag = false;
    // Invalidate one-shot cached observable so a new fetch can occur next session
    this._globalNavOnce$ = null;
  }

  public getGlobalNavEmail(): string { return this._gnEmail; }
  public getGlobalNavFirstName(): string { return this._gnFirstName; }
  public getGlobalNavLastName(): string { return this._gnLastName; }
  public getGlobalNavMessage(): string { return this._gnMessage; }
  public isGlobalNavHxUser(): boolean { return this._gnHxUserFlag; }

  // Fetch GlobalNav user info once per app session; cache with shareReplay(1)
  public fetchGlobalNavUserInfo(): Observable<GlobalNavigationModel | null> {
    if (this._globalNavOnce$) {
      return this._globalNavOnce$;
    }
    const webmethod = (environment.UseTestToken === true) ? '/GlobalNavTest' : '/GlobalNav';
    const service = environment.TokenServiceApi;
    const uri = `${service}${webmethod}`;
    // console.log('GlobalHeaderMenuService.fetchGlobalNavUserInfo - url:', uri);
    this.logger.debug('global-header-menu.service', 'fetchGlobalNavUserInfo', this.subscriptionService.SelectedSubscription, this.subscriptionService.AvailableSubscriptionsString, `calling url: ${uri}`, '');

    this._globalNavOnce$ = this.http.get<any>(uri, { observe: 'response' })
      .pipe(
        map(resp => {
          if (resp instanceof HttpResponse && resp.status === 200) {
            const body = resp.body as GlobalNavigationModel;
            this.setGlobalNavUser(body);
            return body;
          }
          return null;
        }),
        catchError(error => {
          const message = this.getErrorMessage(error);
          this.logger.warn('global-header-menu.service', 'fetchGlobalNavUserInfo', this.subscriptionService.SelectedSubscription, this.subscriptionService.AvailableSubscriptionsString, `Error caught`, message);
          return of(null);
        }),
        take(1),
        shareReplay(1)
      );

    return this._globalNavOnce$;
  }

  private getErrorMessage(error: any): string {
        let message = error.message;
        if ((error.error) && (error.error.message)) {
            message = error.error.message;
        }
        return message;
    }

  
    private handleError(operation: string, error: any) {
        let result: any;
        const response = this._NO_DATA;
        this.logger.warn('user-registration.service', 'handleError', this.subscriptionService.SelectedSubscription, this.subscriptionService.AvailableSubscriptionsString, `Operation:${operation}`, `Server Error:${error}`);
        response.message = error;
        const errorResult = new DataResult(false, 503, 'Service Unavailable error', response);
        result = errorResult;

        return of(result);
    }

}